<script setup lang="ts">
interface BreadcrumbItem {
    '@type': 'ListItem'
    position: number
    name: string
    item: string
}

const { breadcrumbs } = useBreadcrumbs()
const localePath = useLocalePath()
const runtimeConfig = useRuntimeConfig()
const baseUrl = runtimeConfig.public.shopware?.devStorefrontUrl || useRequestURL().origin

const currentPath = computed(() => {
    if (!breadcrumbs.value || breadcrumbs.value.length === 0) {
        return []
    }
    const items: BreadcrumbItem[] = []

    breadcrumbs.value.forEach((pathItem, key) => {
        items.push({
            '@type': 'ListItem',
            position: key + 1,
            name: pathItem.name,
            item: baseUrl + pathItem.path
        })
    })

    return items
})

const structuredDataBreadcrumb = {
    "@context": "https://schema.org",
    '@type': 'BreadcrumbList',
    itemListElement: currentPath
};

// Add JSON-LD to head
useHead({
    script: [
        {
            key: 'structured-breadcrumb-data',
            type: 'application/ld+json',
            innerHTML: structuredDataBreadcrumb
        }
    ]
})
</script>

<template>
    <nav
        class="mx-auto w-full max-w-8xl mb-6"
        aria-label="Breadcrumb"
    >
        <ol class="flex flex-wrap items-center space-x-1 md:space-x-3 hyphens-auto break-words">
            <li class="inline-flex items-center mr-0 my-1">
                <NuxtLink
                    :to="localePath('/')"
                    class="inline-flex items-center text-sm font-medium text-muted-foreground hover:text-primary"
                >
                    <FoundationIcon name="home" class="w-4 h-4 mr-2 shrink-0" />
                    {{ $t('breadcrumbs.home') }}
                </NuxtLink>
                <FoundationIcon name="chevron-right" class="w-4 h-4 mx-1 text-muted-foreground shrink-0" />
            </li>
            <li
                v-for="(breadcrumb, index) in breadcrumbs"
                :key="breadcrumb.path"
                class="inline-flex items-center mr-0 my-1"
            >
                <NuxtLink
                    v-if="breadcrumb.path"
                    :to="localePath(breadcrumb.path)"
                    :class="index === breadcrumbs.length - 1 ? 'font-bold' : 'font-medium'"
                    class="inline-flex items-center text-sm text-muted-foreground hover:text-primary"
                >
                    {{ breadcrumb.name }}
                </NuxtLink>
                <span
                    v-else
                    :class="index === breadcrumbs.length - 1 ? 'font-bold' : 'font-medium'"
                    class="inline-flex items-center text-sm text-foreground"
                >
                    {{ breadcrumb.name }}
                </span>
                <FoundationIcon
                    v-if="index < breadcrumbs.length - 1"
                    name="chevron-right"
                    class="w-4 h-4 mx-1 text-muted-foreground shrink-0"
                />
            </li>
        </ol>
    </nav>
</template>
